import L from 'leaflet';
import TlGreenWaveIcon from '../../../assets/icons/TL_GREEN_WAVE.svg';

const SVG_OPEN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">';

/** Schematic glyphs so each junction class stays readable at map scale. */
const GLYPHS = {
  T: `${SVG_OPEN}<path d="M4 8h16M12 8v10" /></svg>`,
  rotatoria: `${SVG_OPEN}<path d="M12 5a7 7 0 1 1-6.93 8" /><path d="M4 9l1-4 4 1" /></svg>`,
  altro: '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="5.5" /></svg>',
  crossing: '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="5.5" y="4" width="3.4" height="16" rx="1.4" /><rect x="10.3" y="4" width="3.4" height="16" rx="1.4" /><rect x="15.1" y="4" width="3.4" height="16" rx="1.4" /></svg>',
};

const TYPE_VARIANTS = {
  T: 't',
  rotatoria: 'round',
  semaforizzata: 'signal',
};

/** Circle-outline colour per geometric class of a signalized node (Semafix v2 §3). */
export const GEOMETRY_OUTLINE = {
  T: '#3aa0ff',
  rotatoria: '#25c2a0',
  cross: '#e0a400',
  altro: '#c0c4cc',
};

export const GEOMETRY_LABELS = {
  T: 'T',
  rotatoria: 'Rotatoria',
  cross: 'Incrocio',
  altro: 'Altro',
};

/**
 * Traffic-light glyph used inside the signalized marker. Coloured lenses mean
 * satellite-derived information is available; grey lenses mean it is not.
 */
export function trafficLightGlyph(colored) {
  const red = colored ? '#ff4d4f' : '#b4b8be';
  const yellow = colored ? '#ffd400' : '#cfd3d8';
  const green = colored ? '#37d067' : '#e6e9ec';
  return `<svg viewBox="0 0 24 24" fill="none">
    <rect x="8.3" y="2.4" width="7.4" height="19.2" rx="3.2" fill="#20242b" stroke="#fff" stroke-width="1.1" />
    <circle cx="12" cy="7" r="2.1" fill="${red}" />
    <circle cx="12" cy="12" r="2.1" fill="${yellow}" />
    <circle cx="12" cy="17" r="2.1" fill="${green}" />
  </svg>`;
}

function buildIcon(html, size = 30) {
  return L.divIcon({
    className: 'semafix-marker-host',
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    tooltipAnchor: [0, -(size / 2)],
  });
}

/**
 * Single source of truth for "what does this node type look like" —
 * shared by the map markers (intersectionIcon) and the filter bar so
 * the two stay visually identical.
 */
export function nodeVisual(type) {
  const variant = TYPE_VARIANTS[type] || 'other';
  if (type === 'semaforizzata') {
    return { variant, isImage: true, src: TlGreenWaveIcon };
  }
  return { variant, isImage: false, html: GLYPHS[type] || GLYPHS.altro };
}

export function intersectionIcon(intersection, { selected = false, dimmed = false, muted = false, order = null, planStatus = null, greenWaveStatus = null } = {}) {
  const badge = order != null ? `<b class="semafix-marker-order">${order}</b>` : '';

  // Semafix v2 §3: signalized nodes use a common traffic-light marker in a
  // circle. Icon colour = satellite-info availability; outline = geometric type.
  if (intersection.type === 'semaforizzata') {
    const geo = intersection.geometryType || 'altro';
    const outline = GEOMETRY_OUTLINE[geo] || GEOMETRY_OUTLINE.altro;
    const available = Boolean(intersection.satelliteAvailable);
    const classNames = ['semafix-marker', 'signal'];
    if (!available) classNames.push('no-satellite');
    // The border stays the geometric class. Plan status tints the signal
    // symbol itself: green when optimized, red when still to optimize.
    const status = planStatus || greenWaveStatus;
    if (status === 'optimized') classNames.push('plan-optimized');
    else if (status === 'pending') classNames.push('plan-pending');
    if (selected) classNames.push('selected');
    if (dimmed) classNames.push('dimmed');
    if (muted && !selected) classNames.push('muted');
    return buildIcon(`<span class="${classNames.join(' ')}" style="border-color:${outline}">${trafficLightGlyph(available)}${badge}</span>`);
  }

  const { variant, isImage, html, src } = nodeVisual(intersection.type);
  const glyph = isImage ? `<img class="semafix-marker-img" src="${src}" alt="" />` : html;
  const classNames = ['semafix-marker', variant];
  if (selected) classNames.push('selected');
  if (dimmed) classNames.push('dimmed');
  if (muted && !selected) classNames.push('muted');
  return buildIcon(`<span class="${classNames.join(' ')}">${glyph}${badge}</span>`);
}

export function crossingIcon(crossing, { selected = false, muted = false } = {}) {
  const risk = crossing.risk === 'alto' ? 'high' : crossing.risk === 'medio' ? 'medium' : 'low';
  const classNames = ['semafix-marker', 'crossing', risk];
  if (selected) classNames.push('selected');
  if (muted && !selected) classNames.push('muted');
  return buildIcon(`<span class="${classNames.join(' ')}">${GLYPHS.crossing}</span>`, 26);
}

export function trafficLightEditorIcon() {
  return L.divIcon({
    className: 'semafix-editor-traffic-host',
    html: `<span class="semafix-editor-traffic-marker"><img src="${TlGreenWaveIcon}" alt="" /></span>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    tooltipAnchor: [0, -18],
  });
}

export { GLYPHS, TlGreenWaveIcon };
